/**
 * Poll the third-party delivery state for a courier-fulfilled run.
 *
 * The feature is polling-based by convention (no subscriptions in
 * partsDelivery); 10s matches the run-detail poll cadence. Polling stops on
 * terminal states (delivered / canceled / returned / booking FAILED) and can
 * be nudged with refresh() (the detail page's ⟳).
 *
 * SIDE EFFECT (interim until the backend run-status sync job — plan M5): as the
 * courier progresses, the run's single stop is mirrored onto
 * mobility_stops.status (picked_up → in_transit_to, arriving → arrived,
 * delivered → completed) and the run itself is closed out on terminal states —
 * so stop cards and list metrics stay truthful. Writes are idempotent and
 * guarded per transition, safe under multiple pollers (list card + detail).
 */

import React from 'react';
import {
  useSyncCourierStopStatusMutation,
  useSyncCourierOrdersInTransitMutation,
  useSyncCourierOrdersDeliveredMutation,
} from '../queries/syncCourierStopStatus.mutation.generated';
import { useUpdatePartsRunMutation } from '../queries/updatePartsRun.mutation.generated';
import { getThirdPartyClient } from './client';
import { deriveTpStatus, stopStatusFor } from './statusDerivation';
import type { TpDeliveryState } from './types';

const POLL_MS = 10_000;

export function useThirdPartyDelivery(runId: number | null | undefined, enabled: boolean) {
  const [state, setState] = React.useState<TpDeliveryState | null>(null);
  const [loading, setLoading] = React.useState(false);
  const terminalRef = React.useRef(false);
  const syncedStopStatusRef = React.useRef<string | null>(null);
  const syncedOrderStatusRef = React.useRef<string | null>(null);
  const runClosedRef = React.useRef(false);

  const [syncStopStatus] = useSyncCourierStopStatusMutation();
  const [syncOrdersInTransit] = useSyncCourierOrdersInTransitMutation();
  const [syncOrdersDelivered] = useSyncCourierOrdersDeliveredMutation();
  const [updateRun] = useUpdatePartsRunMutation();

  const fetchOnce = React.useCallback(async () => {
    if (!runId || !enabled) return;
    setLoading(true);
    try {
      const next = await getThirdPartyClient().getStatusForRun(runId);
      setState(next);
      const derived = next ? deriveTpStatus(next.orchestration_status, next.delivery_status) : null;
      terminalRef.current = derived ? derived.terminal : false;

      // Mirror courier progress onto the shell run's stop, its orders, and
      // (on terminal states) the run itself.
      if (next && derived) {
        const stopStatus = stopStatusFor(next.delivery_status);
        if (stopStatus && stopStatus !== syncedStopStatusRef.current) {
          syncedStopStatusRef.current = stopStatus;
          syncStopStatus({ variables: { runId, status: stopStatus } }).catch(() => {
            syncedStopStatusRef.current = null; // retry on the next poll
          });
        }

        // Orders: courier has the packages -> in_transit; delivered -> delivered
        // (+ delivery_status/delivered_at). Canceled/returned deliveries leave
        // orders 'assigned'/'in_transit' on purpose — the dealer re-dispatches.
        const orderStatus =
          next.delivery_status === 'delivered'
            ? 'delivered'
            : next.delivery_status === 'picked_up' || next.delivery_status === 'arriving_dropoff'
              ? 'in_transit'
              : null;
        if (orderStatus && orderStatus !== syncedOrderStatusRef.current) {
          syncedOrderStatusRef.current = orderStatus;
          const sync =
            orderStatus === 'delivered'
              ? syncOrdersDelivered({
                  variables: { runId, deliveredAt: next.completed_at || new Date().toISOString() },
                })
              : syncOrdersInTransit({ variables: { runId } });
          sync.catch(() => {
            syncedOrderStatusRef.current = null; // retry on the next poll
          });
        }

        if (derived.terminal && !runClosedRef.current) {
          runClosedRef.current = true;
          const runStatus = derived.bucket === 'completed' ? 'completed' : 'cancelled';
          updateRun({ variables: { id: runId, set: { status: runStatus } } }).catch(() => {
            runClosedRef.current = false;
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [runId, enabled, syncStopStatus, updateRun]);

  React.useEffect(() => {
    // No state reset here (consumers mount one hook per run); the first fetch
    // overwrites. Reset the latches so a new run polls and syncs again. The
    // first fetch is deferred a tick so no setState runs synchronously in the
    // effect.
    terminalRef.current = false;
    syncedStopStatusRef.current = null;
    syncedOrderStatusRef.current = null;
    runClosedRef.current = false;
    if (!runId || !enabled) return;
    const kickoff = setTimeout(fetchOnce, 0);
    const timer = setInterval(() => {
      if (!terminalRef.current) fetchOnce();
    }, POLL_MS);
    return () => {
      clearTimeout(kickoff);
      clearInterval(timer);
    };
  }, [runId, enabled, fetchOnce]);

  return { delivery: state, loading, refresh: fetchOnce };
}
